import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, IconButton } from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import PropertyFooterLayout from '../../../components/properties/PropertyFooterLayout';
import { savePhotosToIndexedDB, getPhotosFromIndexedDB, deleteAllPhotosFromIndexedDB } from '../../../utils/indexDB';

const progress = 30;
const step = 2;

type PublishOutletContext = {
  handleNavigateToPropertyTitle: () => void;
};

type FileWithId = {
  id: string;
  file: File;
};

const PropertyUploadPhotosPage: React.FC = () => {
  const { handleNavigateToPropertyTitle } = useOutletContext<PublishOutletContext>();
  const [localFiles, setLocalFiles] = useState<FileWithId[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadPhotos = async () => {
      const files = await getPhotosFromIndexedDB();
      const newFiles = files.map((file, index) => ({
        id: `${index}-${Date.now()}`,
        file,
      }));
      setLocalFiles(newFiles);
    };

    loadPhotos();
  }, []);

  const syncIndexedDB = async (files: FileWithId[]) => {
    await deleteAllPhotosFromIndexedDB();
    await savePhotosToIndexedDB(files.map((file) => file.file));
  };

  useEffect(() => {
    syncIndexedDB(localFiles);
  }, [localFiles]);

  const onDrop = (acceptedFiles: File[]) => {
    const uniqueFiles = acceptedFiles.filter((newFile) => {
      return !localFiles.some(
        (existingFile) =>
          existingFile.file.name === newFile.name && existingFile.file.size === newFile.size
      );
    });

    if (uniqueFiles.length > 0) {
      const updatedFiles = [
        ...localFiles,
        ...uniqueFiles.map((file) => ({
          id: Date.now().toString(),
          file,
        })),
      ].slice(0, 10); // Limitar a 10 archivos
      setLocalFiles(updatedFiles);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    onDrop,
    multiple: true,
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = () => {
    if (localFiles.length >= 5) {
      handleNavigateToPropertyTitle();
    } else {
      alert('Debes subir al menos 5 fotos.');
    }
  };

  const removeFile = async (index: number) => {
    const newFiles = localFiles.filter((_, i) => i !== index);
    setLocalFiles(newFiles);
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
        px:4
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 10 }}>
        <Typography variant="h4" gutterBottom>
          Agrega algunas fotos de tu apartamento
        </Typography>
        <Typography variant="body1" gutterBottom>
          Para empezar, necesitarás cinco fotos. Después podrás agregar más o hacer cambios.
        </Typography>

        <Box
          {...getRootProps()}
          sx={{
            mt: 4,
            p: 2,
            border: '0.5px dashed grey',
            borderRadius: 2,
            backgroundColor: 'whitesmoke',
            cursor: 'pointer',
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: localFiles.length > 0 ? 'flex-start' : 'center',
            position: 'relative',
          }}
        >
          <input {...getInputProps()} />
          {localFiles.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CameraAltIcon sx={{ fontSize: '3rem', color: 'grey.600' }} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Arrastra y suelta tus fotos aquí o haz clic para seleccionar
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              marginTop: localFiles.length > 0 ? '20px' : 0,
            }}
          >
            {localFiles.map((file, index) => (
              <Box
                key={file.id}
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '150px',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'lightgrey',
                }}
              >
                <img
                  src={URL.createObjectURL(file.file)}
                  alt={`Uploaded file ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <PropertyFooterLayout
        progress={progress}
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={localFiles.length < 5}
      />
    </Container>
  );
};

export default PropertyUploadPhotosPage;
